import {
    AfterViewInit,
    Component,
    inject,
    signal,
    ViewChild,
} from '@angular/core';
import {
    ContentService,
    LayoutModule,
    NotificationsService,
    SharedUiModule,
    TemplateDrawerComponent,
} from '@pf/shared-ui';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NotificationType, ToastMessage, ToastType } from '@pf/shared-common';
import { PrintLabelOptions, PrintOptions } from '../models/print-options.model';
import { PrintJobService } from '../services/print-job.service';
import { PrinterPreferencesService } from '../services/printer-preferences.service';
import { ZplPreviewComponent } from './zpl-preview.component';
import {
    PrintLabelSettingsComponent,
    PrintSettings,
    PrintSettingsChangeEvent,
} from './print-label-settings.component';
import { AutoPrintCountdownComponent } from './auto-print-countdown.component';
import { tap } from 'rxjs';

@Component({
    selector: 'platform-print-label',
    imports: [
        LayoutModule,
        SharedUiModule,
        ZplPreviewComponent,
        PrintLabelSettingsComponent,
        AutoPrintCountdownComponent,
    ],
    standalone: true,
    providers: [ContentService],
    templateUrl: './print-label.component.html',
    styleUrl: './print-label.component.scss',
})
export class PrintLabelComponent
    implements TemplateDrawerComponent<PrintOptions>, AfterViewInit
{
    readonly data: PrintOptions = inject(NZ_DRAWER_DATA);
    private readonly printJobService = inject(PrintJobService);
    private readonly notification = inject(NotificationsService);
    private readonly printerPreferences = inject(PrinterPreferencesService);
    drawerRef = inject(NzDrawerRef);

    @ViewChild(AutoPrintCountdownComponent)
    private autoPrintRef?: AutoPrintCountdownComponent;

    printSettings = signal<PrintSettings>({
        copies: 1,
        options: {
            printerAddress: '',
            printerUid: '',
        },
    });

    label = signal<PrintLabelOptions | null>(null);
    printing = signal(false);

    constructor() {
        this.label.set(this.data.labels?.[0] ?? null);
        this.seedFromPreferences();
    }

    ngAfterViewInit(): void {
        if (this.shouldAutoPrint()) {
            this.autoPrintRef?.start();
        }
    }

    printLabel() {
        this.autoPrintRef?.stop();
        const { options, copies } = this.printSettings();
        if (!options.printerUid && !options.printerAddress) {
            this.notification.create({
                type: NotificationType.ToastMessage,
                toastType: ToastType.warning,
                message: 'Please check the printer settings.',
                duration: 2000,
            } as ToastMessage);
            return;
        }

        const label = this.label();

        if (!label) {
            return;
        }

        this.printing.set(true);
        this.printJobService
            .createPrintJob([label], copies, options)
            .pipe(
                tap(() => {
                    this.printing.set(false);
                })
            )
            .subscribe();
    }

    protected onSettingsChanged($event: PrintSettingsChangeEvent) {
        this.printSettings.set($event);
    }

    private seedFromPreferences() {
        const prefs = this.printerPreferences.get();
        if (!prefs.printerAddress && !prefs.printerUid) {
            return;
        }
        this.printSettings.set({
            copies: prefs.copies || 1,
            options: {
                printerAddress: prefs.printerAddress,
                printerUid: prefs.printerUid,
            },
        });
    }

    private shouldAutoPrint(): boolean {
        if (!this.data.autoPrint) {
            return false;
        }
        const prefs = this.printerPreferences.get();
        if (!prefs.autoPrint) {
            return false;
        }
        if (!prefs.printerAddress && !prefs.printerUid) {
            return false;
        }
        return !!this.label();
    }
}
